import { useState, createContext, useCallback, useEffect, useRef } from "react";
import { createClient } from "@supabase/supabase-js";
import Loading from "pages/Loading";
import { addSampleTeam as addSampleTeamToState, bootstrapTrialStore, buildFakeUserFromTrial, clearTrialStore, loadTrialStore, saveTrialStore } from "./TrialStore";
import { makeMockSupabase } from "./MockSupabase";

export const AuthContext = createContext();

export const AuthContextProvider = ({ children }) => {
  const [supabase, setSupabase] = useState(null);
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState(null);
  const [isTrial, setIsTrial] = useState(false);
  const realSupabaseRef = useRef(null);
  const trialStateRef = useRef(null);

  useEffect(() => {
    async function fetchCreds() {
      fetch("/.netlify/functions/creds", {
        method: "GET",
      })
        .then((res) => res.json())
        .then((data) => {
          const client = createClient(data.url, data.key);
          realSupabaseRef.current = client;
          setSupabase(client);
        });
    }
    fetchCreds();

    // ** For local development without Netlify dev **
    // This would bypass the creds endpoint and use local Supabase only
    // Note: This would break storage access since local storage is empty
    // setSupabase(
    //   createClient(
    //     "http://127.0.0.1:54321",
    //     "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZS1kZW1vIiwicm9sZSI6ImFub24iLCJleHAiOjE5ODM4MTI5OTZ9.CRXP1A7WOeoJeXxjNni43kdQwgnWNReilDMblYTn_I0"
    //   )
    // );
  }, []);

  const enterTrialMode = useCallback(() => {
    let trial = loadTrialStore();
    if (!trial) trial = bootstrapTrialStore();
    trialStateRef.current = trial;
    const mock = makeMockSupabase(trial, {
      onSignOut: () => {
        // Reload to re-bootstrap a clean trial.
        if (typeof window !== "undefined") window.location.replace("/");
      },
    });
    setSupabase(mock);
    setUser(buildFakeUserFromTrial(trial));
    setIsTrial(true);
    setLoading(false);
  }, []);

  const logout = useCallback(() => {
    if (isTrial) {
      clearTrialStore();
      if (typeof window !== "undefined") window.location.replace("/");
      return;
    }
    realSupabaseRef.current?.auth.signOut();
  }, [isTrial]);

  // Refresh the in-memory user from the trial store. Called after any trial
  // mutation that requires the user shape to be re-derived (e.g. avatar set).
  // We copy fields into the SAME object the mock client holds a reference to,
  // rather than replacing the ref, so the mock's view stays in sync.
  const refreshTrialUser = useCallback(() => {
    if (!trialStateRef.current) return;
    const fresh = loadTrialStore();
    if (fresh) {
      Object.keys(trialStateRef.current).forEach((k) => delete trialStateRef.current[k]);
      Object.assign(trialStateRef.current, fresh);
    }
    setUser(buildFakeUserFromTrial(trialStateRef.current));
  }, []);

  // Mutate the trial state in-place to add a sample team, then refresh the
  // in-memory user. Used by the welcome popup.
  const addTrialSampleTeam = useCallback(() => {
    if (!trialStateRef.current) return;
    addSampleTeamToState(trialStateRef.current);
    setUser(buildFakeUserFromTrial(trialStateRef.current));
  }, []);

  const getSession = useCallback(async () => {
    const realClient = realSupabaseRef.current;
    if (!realClient) return;

    const _res = await realClient.auth.getSession();
    const { data } = _res;

    if (data.session) {
      // Real session: leave trial mode if we were in it. Trial blob stays
      // until migration in SetOrgAndProj. Setting state to the same value
      // is a no-op if we were not in trial mode.
      trialStateRef.current = null;
      setIsTrial(false);
      setSupabase(realClient);
      const people = await realClient.from("people").select().eq("uid", data.session?.user.id);

      // If for some reason no user from db, logout
      if (!people.data.length) {
        realClient.auth.signOut();
        return;
      }

      const memberships = await realClient
        .from("memberships")
        .select("*, orgs(*), people(*, days(*))")
        .eq("person_id", people.data?.[0]?.id);

      // Set current org to user
      const currOrg = memberships.data.find((mem) => {
        return mem.org_id === people.data[0].current_org_id && !["deleted"].includes(mem.status) && !["deleted"].includes(mem.orgs.status);
      });

      // Filter embedded settings to only those owned by the current user or project-wide (user_id is null).
      // Without this, user.proj.settings would contain rows from all users on the project, causing
      // per-user setting reads via .find(s => s.name === ...) to potentially return another user's value.
      const projects = currOrg
        ? await realClient
            .from("projects")
            .select("*, settings(*)")
            .eq("org_id", currOrg.orgs.id)
            .or(`user_id.eq.${people.data[0].id},user_id.is.null`, { foreignTable: "settings" })
            .order("id", { ascending: true })
        : null;

      // Filter projects to those where the user has any days entries
      const membership = memberships.data.find((mem) => mem.org_id === currOrg.orgs.id);
      const userDays = membership?.people?.days || [];
      const filteredProjects = projects?.data ? projects.data.filter((proj) => userDays.some((day) => day.project_id === proj.id)) : [];

      // Set current project to user
      let currProj = filteredProjects.find((proj) => proj.id === people.data[0]?.current_proj_id) || null;
      if (filteredProjects.length && !currProj) {
        currProj = filteredProjects.pop();
        await realClient.from("people").update({ current_proj_id: currProj.id }).eq("id", people.data[0].id);
      }
      if (!currProj) currProj = { id: -1, settings: [] };

      const image = await realClient.from("images").select("url").eq("ownerid", people.data?.[0]?.id).eq("type", "avatar");
      let signedUrl = null;
      if (image.data?.[0]) {
        try {
          const result = await realClient.storage.from("avatars").createSignedUrl(image.data[0].url, 3600);
          if (result.data) {
            signedUrl = result.data;
          }
        } catch (error) {
          console.warn("Failed to create signed URL for avatar:", error);
          // Continue without avatar URL rather than failing the entire session
        }
      }

      // Load organization logo
      const orgLogo = await realClient.from("images").select("url").eq("org_id", currOrg?.orgs?.id).eq("type", "logo");
      let orgLogoSignedUrl = null;
      if (orgLogo.data?.[0]) {
        try {
          const result = await realClient.storage.from("logos").createSignedUrl(orgLogo.data[0].url, 3600);
          if (result.data) {
            orgLogoSignedUrl = result.data;
          }
        } catch (error) {
          console.warn("Failed to create signed URL for logo:", error);
          // Continue without logo URL rather than failing the entire session
        }
      }

      setUser({
        ...people.data[0],
        access_token: data.session.access_token,
        avatar: signedUrl?.signedUrl,
        id: people.data?.[0]?.id,
        memberships: memberships.data?.length ? memberships.data : null,
        org: currOrg?.orgs,
        proj: currProj,
        projects: filteredProjects,
        created: data.session.user.created_at,
        last_signin: data.session.user.last_sign_in_at,
        role: memberships?.data?.find((org) => org.id === currOrg.id)?.type,
        orgLogo: orgLogoSignedUrl?.signedUrl,
      });
    } else {
      // No real session. Drop into trial mode so the unauthed visitor lands
      // straight in the Timeline. Guard against re-entry: re-subscribing to
      // onAuthStateChange (which we do whenever `supabase` flips to the mock)
      // will re-fire INITIAL_SESSION, and without this guard we'd loop.
      if (trialStateRef.current) return;
      enterTrialMode();
    }
  }, [enterTrialMode]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const realClient = realSupabaseRef.current;
    if (realClient) {
      const {
        data: { subscription },
      } = realClient.auth.onAuthStateChange((_event, session) => {
        getSession();
      });

      return () => subscription.unsubscribe();
    }
  }, [supabase, getSession]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (supabase && user) setLoading(false);
  }, [supabase, user]);

  // CALLBACKS   XXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXX

  const roleAccess = useCallback(
    (minimumRole) => {
      const userRoleIndex = ["member", "lead", "manager", "admin", "owner"].indexOf(user.role);
      const minRoleIndex = ["member", "lead", "manager", "admin", "owner"].indexOf(minimumRole);
      return userRoleIndex >= minRoleIndex;
    },
    [user]
  );

  const join = useCallback(
    async (email, password, cb) => {
      const realClient = realSupabaseRef.current;
      if (!realClient) {
        cb && cb({ message: "auth not ready" });
        return;
      }
      const { error } = await realClient.auth.signUp({
        email: email,
        password: password,
        options: {
          emailRedirectTo: "https://peepz.netlify.app/auth",
        },
      });

      if (cb) cb(error || null);
    },
    []
  );

  const login = useCallback(
    async (username, pass, cb) => {
      const realClient = realSupabaseRef.current;
      if (!realClient) {
        cb && cb({ message: "auth not ready" });
        return;
      }
      realClient.auth
        .signInWithPassword({
          email: username,
          password: pass,
        })
        .then((res) => {
          cb(res.error);
        });
    },
    []
  );

  const requestPasswordRecovery = useCallback(
    (email, cb) => {
      const realClient = realSupabaseRef.current;
      if (!realClient) {
        cb && cb("auth not ready");
        return;
      }
      realClient.auth
        .resetPasswordForEmail(email, {
          redirectTo: "http://peepz.netlify.app/password",
        })
        .then((res) => {
          if (cb) cb(res.error?.message || false);
        });
    },
    []
  );

  const updatePassword = useCallback(
    (password, cb) => {
      const realClient = realSupabaseRef.current;
      if (!realClient) {
        cb && cb("auth not ready");
        return;
      }
      realClient.auth.updateUser({ password: password }).then((res) => {
        if (cb) cb(res.error?.message || null);
      });
    },
    []
  );

  // Update the current user's people row. Works in both real and trial modes.
  const updateUser = useCallback(
    (data, cb) => {
      if (isTrial) {
        const state = trialStateRef.current;
        if (state) {
          const person = state.people.find((p) => p.id === state.user.id);
          if (person) {
            Object.assign(person, data);
            saveTrialStore(state);
            refreshTrialUser();
          }
        }
        if (cb) cb(null);
        return;
      }
      supabase
        .from("people")
        .update(data)
        .eq("id", user.id)
        .then((res) => {
          getSession();
          if (cb) cb(res.error?.message || null);
        });
    },
    [supabase, user, getSession, isTrial, refreshTrialUser]
  );

  const setUserMembership = useCallback(
    (status, memId) => {
      if (isTrial) return; // trial has no real invitations
      supabase
        .from("memberships")
        .update({ status: status })
        .eq("id", memId)
        .then(() => {
          getSession();
        });
    },
    [supabase, getSession, isTrial]
  );

  const refresh = useCallback(() => {
    if (isTrial) {
      refreshTrialUser();
      return;
    }
    getSession();
  }, [getSession, isTrial, refreshTrialUser]);

  const contextValues = {
    supabase,
    user,
    isTrial,
    realSupabase: realSupabaseRef.current,
    login,
    logout,
    join,
    requestPasswordRecovery,
    updatePassword,
    updateUser,
    setUserMembership,
    roleAccess,
    refresh,
    addTrialSampleTeam,
  };

  return (
    <>
      <AuthContext.Provider value={contextValues}>
        {!loading && children}
        {loading && <Loading />}
      </AuthContext.Provider>
    </>
  );
};
